import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostBinding, inject, Input, OnInit, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { FieldComponent } from '../field/field.component'

/**
 * @public
 */
export type CheckboxElement = NgElement & WithProperties<CheckboxComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: [ './checkbox.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [ CommonModule ]
})
export class CheckboxComponent extends FieldComponent implements OnInit, AfterViewInit {

  static readonly DEFAULT_CHECKBOX_STATE = false

  private readonly _renderer2 = inject(Renderer2)

  @ViewChild('input')
  private readonly _inputRef: ElementRef<HTMLInputElement>

  private _checked
  private _checkedValue: string = 'true'
  private _uncheckedValue: string = 'false'

  protected _elementRef = inject(ElementRef)

  ngOnInit(): void {
    // If the checkbox is in its indeterminate state
    if (this.isIndeterminate) {
      this.checked = CheckboxComponent.DEFAULT_CHECKBOX_STATE
    }
  }

  ngAfterViewInit(): void {
    this.syncValidity(this._inputRef?.nativeElement)
  }

  /**
   * Lifecycle event that is triggered when the host of the component is clicked
   *
   * @param event   The DOM Event of the click event
   */
  onInput(event: Event): void {
    // Pause propagation of input event
    event.stopPropagation()
    let target = this._inputRef.nativeElement

    // Set the value
    this.checked = !this.checked

    // Mark it as touched
    this.touched = true

    // Update the validity of the element
    this.syncValidity(target)

    // Detect changes
    this._changeDetectorRef.detectChanges()

    // Resume propagation of input event
    this._elementRef.nativeElement.dispatchEvent(new Event('input', { ...event, bubbles: true }))
  }

  /**
   * Synchronize the validity of the element with the validity state of the provided element
   *
   * @param element The target element
   */
  private syncValidity(element: HTMLInputElement): void {
    this.setValidity(
      element?.validity,
      element?.validationMessage,
      element
    )
  }

  @Input()
  @HostBinding()
  set checked(value: string | boolean) {
    let pendingValue = value

    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._checked = pendingValue

    if (this._inputRef?.nativeElement) {
      this._inputRef.nativeElement.checked = this.checked
    }

    this.value = this.checked ? this.checkedValue : this.uncheckedValue

    /**
     * When working with slotted components in another component the event fires before the {@link Input} annotation
     * is able to update the native element.
     *
     * So we manually update the property with {@link Renderer2} so that it's confirmed set before we fire off the input event.
     */
    this._renderer2.setProperty(this._elementRef.nativeElement, 'checked', this.checked)
    this._renderer2.setProperty(this._elementRef.nativeElement, 'value', this.value)
  }

  get checked(): boolean {
    return this._checked ?? false
  }

  @Input()
  @HostBinding()
  set checkedValue(value: string) {
    if (value !== undefined && value !== this._checkedValue) {
      this._checkedValue = value

      if (this.checked) {
        this.value = this._checkedValue
      }
    }
  }

  get checkedValue(): string {
    return this._checkedValue as string
  }

  @Input()
  @HostBinding()
  set uncheckedValue(value: string) {
    if (value !== undefined && value !== this._uncheckedValue) {
      this._uncheckedValue = value

      if (!this.checked) {
        this.value = this._uncheckedValue
      }
    }
  }

  get uncheckedValue(): string {
    return this._uncheckedValue as string
  }

  get isIndeterminate(): boolean {
    return this.value !== this.checkedValue && this.value !== this.uncheckedValue
  }

  @Input()
  @HostBinding()
  set value(value: string) {
    super.value = value
    this._changeDetectorRef.detectChanges()
    this.syncValidity(this._inputRef?.nativeElement)
  }

  get value(): string {
    return super.value
  }
}

/**
 * @public
 */
export const registerCheckboxElement = registerCustomElementFactory(CheckboxComponent)
